import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import type { Template } from '@/types/api';
import {
  DESIGN_CANVAS_HEIGHT_PX,
  DESIGN_CANVAS_WIDTH_PX,
  asRecord,
  layoutStyleFromElementDef,
  stackingZIndex,
} from '@/creativeComposition/elementMerge';

const ELEMENT_TYPE_STYLES: Record<string, string> = {
  text: 'border-primary/40 bg-primary/10',
  image: 'border-emerald-400/40 bg-emerald-400/10',
  video: 'border-sky-400/40 bg-sky-400/10',
  logo: 'border-violet-400/40 bg-violet-400/10',
  qr: 'border-amber-400/40 bg-amber-400/10',
  color_fill: 'border-white/15 bg-white/5',
};

function isBackgroundName(name: string): boolean {
  return name.trim().toLowerCase() === 'background';
}

interface TemplatePreviewProps {
  template: Template;
  className?: string;
}

export function TemplatePreview({ template, className = '' }: TemplatePreviewProps) {
  const renderSettings = template.render_settings ?? {};
  const canvasWidth = Number(renderSettings.width_px ?? DESIGN_CANVAS_WIDTH_PX);
  const canvasHeight = Number(renderSettings.height_px ?? DESIGN_CANVAS_HEIGHT_PX);
  const elements = template.elements;

  const normalizedElements = useMemo(() => {
    if (!Array.isArray(elements)) return [];
    return elements
      .map((raw, index) => {
        const def = asRecord(raw);
        if (!def) return null;
        const elementType = String(def.element_type ?? '');
        const elementName = String(def.element_name ?? elementType ?? `Element ${index + 1}`);
        const isBackground = isBackgroundName(elementName);
        const data = asRecord(def.element_data) ?? {};
        const color = asRecord(data.color_fill_element)?.color as string | undefined;
        const style: CSSProperties = {
          ...layoutStyleFromElementDef(def, canvasWidth, canvasHeight),
          zIndex: stackingZIndex(def),
          backgroundColor: color,
        };
        return {
          key: `${elementName}-${index}`,
          elementType,
          elementName,
          isBackground,
          style,
        };
      })
      .filter(Boolean) as Array<{
      key: string;
      elementType: string;
      elementName: string;
      isBackground: boolean;
      style: CSSProperties;
    }>;
  }, [canvasHeight, canvasWidth, elements]);

  return (
    <div className={`relative h-full w-full overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-surface-container-low to-surface-container" />
      {normalizedElements.map((el) => {
        const typeStyle = ELEMENT_TYPE_STYLES[el.elementType] ?? 'border-white/15 bg-white/5';
        return (
          <div
            key={el.key}
            className={`absolute rounded border ${typeStyle} ${
              el.isBackground ? 'border-transparent' : ''
            }`}
            style={el.style}
          >
            {!el.isBackground && (
              <span className="absolute inset-0 flex items-center justify-center px-1">
                <span className="rounded bg-black/60 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-white/80">
                  {el.elementName}
                </span>
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
